import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type Status = 'checking' | 'needs_profile' | 'ready' | { error: string };

type Props = {
  session: Session;
  children: React.ReactNode;
};

// Ensures every authenticated session has a matching `users` row. Web sign-ups
// land here with no profile — prompt for a display name before the app opens,
// otherwise testers would show up in crews nameless.
export function AccountBootstrap({ session, children }: Props) {
  const [status, setStatus] = useState<Status>('checking');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus('checking');
    (async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setStatus({ error: error.message });
        return;
      }
      setStatus(data ? 'ready' : 'needs_profile');
    })();
    return () => {
      cancelled = true;
    };
  }, [session.user.id]);

  async function createProfile(e: React.FormEvent) {
    e.preventDefault();
    const name = displayName.trim();
    if (!name) return;
    setBusy(true);
    const { error } = await supabase.from('users').insert({
      id: session.user.id,
      name: slugify(name),
      display_name: name,
    });
    if (error) {
      setStatus({ error: error.message });
      setBusy(false);
      return;
    }
    setStatus('ready');
    setBusy(false);
  }

  if (status === 'checking') {
    return (
      <div className="app-loading">
        <div className="spinner" />
        <style>{`
          .app-loading { min-height: 100vh; display: flex; align-items: center; justify-content: center; }
          .spinner {
            width: 28px; height: 28px;
            border: 3px solid var(--divider);
            border-top-color: var(--accent);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (typeof status === 'object' && 'error' in status) {
    return (
      <div className="bootstrap-wrap">
        <p className="bootstrap-error">{status.error}</p>
        <button type="button" className="bootstrap-link" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
        <BootstrapStyles />
      </div>
    );
  }

  if (status === 'needs_profile') {
    return (
      <div className="bootstrap-wrap">
        <h1>What should we call you?</h1>
        <p className="bootstrap-sub">This is the name your groups will see.</p>
        <form onSubmit={createProfile} className="bootstrap-form">
          <input
            type="text"
            placeholder="Your name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoCapitalize="words"
            autoFocus
            required
          />
          <button
            type="submit"
            disabled={!displayName.trim() || busy}
            className="bootstrap-submit"
          >
            {busy ? '…' : 'Continue'}
          </button>
        </form>
        <BootstrapStyles />
      </div>
    );
  }

  return <>{children}</>;
}

function BootstrapStyles() {
  return (
    <style>{`
      .bootstrap-wrap {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 32px 24px;
        gap: 8px;
      }
      .bootstrap-wrap h1 {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        color: var(--text);
        text-align: center;
      }
      .bootstrap-sub {
        color: var(--text-muted);
        font-size: 14px;
        margin: 0 0 16px;
        text-align: center;
      }
      .bootstrap-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        max-width: 340px;
      }
      .bootstrap-form input {
        background: var(--surface);
        color: var(--text);
        border: 1px solid var(--divider);
        border-radius: 10px;
        padding: 14px;
        font-size: 16px;
      }
      .bootstrap-form input:focus { outline: none; border-color: var(--accent); }
      .bootstrap-submit {
        background: var(--accent);
        color: var(--bg);
        border: none;
        border-radius: 10px;
        padding: 14px;
        font-size: 16px;
        font-weight: 700;
      }
      .bootstrap-submit:disabled { opacity: 0.5; }
      .bootstrap-error { color: var(--error); font-size: 14px; text-align: center; }
      .bootstrap-link {
        background: transparent;
        color: var(--accent);
        border: none;
        padding: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
      }
    `}</style>
  );
}

function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}
